import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { useTheme } from '../context/ThemeContext';
import { parse3MF } from '../utils/threeMF';

const MATERIAL_COLOR = 0x6366f1;

/**
 * Parse a 3D model blob into a THREE.Object3D
 */
const parseModel = async (blob, extension) => {
  const buffer = await blob.arrayBuffer();
  const material = new THREE.MeshStandardMaterial({ color: MATERIAL_COLOR, metalness: 0.1, roughness: 0.6 });

  switch (extension) {
    case 'stl': {
      const geometry = new STLLoader().parse(buffer);
      geometry.computeVertexNormals();
      return new THREE.Mesh(geometry, material);
    }
    case 'obj': {
      const object = new OBJLoader().parse(new TextDecoder().decode(buffer));
      object.traverse((child) => {
        if (child.isMesh) child.material = material;
      });
      return object;
    }
    case '3mf':
      return parse3MF(buffer, material);
    default:
      throw new Error('Unsupported model format');
  }
};

const ModelViewer = ({ blob, extension }) => {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const { isDark } = useTheme();

  useEffect(() => {
    const container = containerRef.current;
    let renderer;
    let controls;
    let frameId;
    let resizeObserver;
    let disposed = false;

    const init = async () => {
      let model;
      try {
        model = await parseModel(blob, extension?.toLowerCase());
      } catch (err) {
        console.error('Model load error:', err);
        setError('Failed to load 3D model');
        return;
      }
      if (disposed) return;

      // 3MF and many CAD exports use Z-up
      if (extension?.toLowerCase() !== 'obj') model.rotation.x = -Math.PI / 2;

      const scene = new THREE.Scene();
      scene.add(new THREE.HemisphereLight(0xffffff, 0x444444, 1.5));
      const dirLight = new THREE.DirectionalLight(0xffffff, 1.5);
      dirLight.position.set(1, 2, 3);
      scene.add(dirLight);

      // Center the model on the origin
      const wrapper = new THREE.Group();
      wrapper.add(model);
      const box = new THREE.Box3().setFromObject(wrapper);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3()).length() || 1;
      model.position.sub(center);
      scene.add(wrapper);

      const camera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, size / 100, size * 100);
      camera.position.set(size * 0.6, size * 0.5, size * 0.8);
      dirLight.position.copy(camera.position);

      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(container.clientWidth, container.clientHeight);
      container.appendChild(renderer.domElement);

      controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;

      const animate = () => {
        frameId = requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };
      animate();

      resizeObserver = new ResizeObserver(() => {
        camera.aspect = container.clientWidth / container.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(container.clientWidth, container.clientHeight);
      });
      resizeObserver.observe(container);
    };

    setError(null);
    init();

    return () => {
      disposed = true;
      cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      controls?.dispose();
      if (renderer) {
        renderer.dispose();
        renderer.domElement.remove();
      }
    };
  }, [blob, extension]);

  if (error) {
    return <p className="text-center text-red-500 py-12">{error}</p>;
  }

  return (
    <div
      ref={containerRef}
      className={`w-full h-[70vh] rounded-lg shadow-lg ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}
    />
  );
};

export default ModelViewer;
